import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MenuItem, Stack, TextField, Button, IconButton, InputLabel, FormControl, Select, Grid, Box, Typography, FormControlLabel, Switch } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { createTournament, getTournament, updateTournament } from '../../services/Common/tournament.service';
import { getCategoryList } from '../../services/Common/category.service';
import { toast } from 'react-toastify';
import { AiFillDelete } from 'react-icons/ai';
import { nanoid } from "nanoid"
import Page from '../../data/Page';

const levelOptions = [
    { label: 'Multimap', value: 'multimap' },
    { label: 'Single Map', value: 'singlemap' }
];

const tournamentStatuses = [
    { label: 'Registration Started', value: 0 },
    { label: 'Registration Close', value: 1 },
    { label: 'Tournament Completed', value: 4 }
]

const teamOptions = [
    { label: 'Solo', value: 'solo' },
    { label: 'Duo', value: 'duo' },
    { label: 'Squad', value: 'squad' },
];

const defaultTags = ["Daily Scrims", "Small Tournaments", "Solo Tournaments", "Duo Tournaments", "Trio Tournaments", "Squad Tournaments"]

export default function TournamentForm({ edit }) {
    const navigate = useNavigate()
    // const { user } = useSelector(state => state.auth)
    const [loading, setLoading] = useState(false) // kept for button loading state in future
    const [currentStatus, setCurrentStatus] = useState(null)
    const [categories, setCategories] = useState([])
    const [formData, setFormData] = useState({
        title: "",
        level_type: "",
        team_type: "",
        team_count: "",
        match_count: "",
        registration_type: 'free',
        subscriberOnly: false,
        entry_fee: 0,
        entry_fee_type: 'ev',
        scoring_config: {
            kill_points: 1,
            position_points: []
        },
        tags: [],
        registration_closed_on: "",
        category: "",
        status: 0,
        prizes: [{
            title: "",
            price: "",
            status: ""
        }],
        details: {
            rules: "",
            points_distribution: [{
                id: nanoid(10),
                title: "",
                points: ""
            }],
            updates: ""
        },
        roadmap: "",
        landscape_img: null,
        portrait_img: null,
    })

    const { id } = useParams();

    useEffect(() => {
        if (edit) {
            (async () => {
                let { data } = await getTournament(id);
                // Support both old and new backend response shapes
                const t = data?.tournament?.tournament || data?.tournament || data;
                setCurrentStatus(t?.status)
                setFormData({
                    title: t?.title || "",
                    level_type: t?.level_type || "",
                    team_type: t?.team_type || "",
                    team_count: t?.team_count || "",
                    match_count: t?.match_count || "",
                    registration_type: t?.registration_type || 'free', subscriberOnly: !!t?.subscriberOnly,
                    entry_fee: typeof t?.entry_fee === 'number' ? t.entry_fee : Number(t?.entry_fee) || 0,
                    entry_fee_type: t?.entry_fee_type || 'ev',
                    scoring_config: t?.scoring_config || { kill_points: 1, position_points: [] },
                    registration_closed_on: t?.registration_closed_on || "",
                    status: t?.status ?? 0,
                    prizes: t?.prizes || [],
                    details: t?.details || { rules: "", points_distribution: [], updates: "" },
                    roadmap: t?.roadmap || "",
                    landscape_img: t?.landscape_img || null,
                    portrait_img: t?.portrait_img || null,
                    category: t?.category?._id || t?.category || "",
                    tags: t?.tags || []
                });
            })()
        }
    }, [edit, id])

    useEffect(() => {
        (async () => {
            const categoryRes = await getCategoryList();
            setCategories(categoryRes)
        })()
    }, [])



    const {
        title,
        level_type,
        team_type,
        team_count,
    // match_count,
    registration_type,
    entry_fee,
        entry_fee_type,
        scoring_config,
        registration_closed_on,
        status,
        prizes,
        details,
        category,
        tags
    } = formData;

    const role = localStorage.getItem("role")

    const disabled = role === "admin" ? false :
        role === "organization" && currentStatus !== 4 ? false : true;

    const handleChange = (e) => {
        let { name, value } = e.target;

        if (name === "portrait_img" || name === "landscape_img")
            value = e.target.files[0];

        if (name === 'registration_type') {
            // When set to free, auto reset fee to 0 and default type
            if (value === 'free') {
                setFormData({ ...formData, registration_type: 'free', entry_fee: 0, entry_fee_type: 'ev' })
                return;
            }
        }

        setFormData({ ...formData, [name]: value })
    }

    const handlePrizeChange = (e, index) => {
        const fieldName = e.target.name

        const newState = {
            ...formData,
            prizes: prizes?.map((prize, i) => i === index ? { ...prize, [fieldName]: e.target.value } : prize)
        }

        setFormData(newState)
    }

    const handlePrizeDistributionChange = (e, index) => {
        const fieldName = e.target.name

        const newState = {
            ...formData,
            details: {
                ...details,
                points_distribution: details.points_distribution?.map((point, i) => i === index ? { ...point, [fieldName]: e.target.value } : point)
            }
        }

        setFormData(newState)
    }

    const handlePositionPointsChange = (e, index) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            scoring_config: {
                ...scoring_config,
                position_points: scoring_config.position_points.map((point, i) => i === index ? { ...point, [name]: value } : point)
            }
        });
    }



    const handleSubmit = async () => {
        setLoading(true)
        const data = new FormData();
        try {
            // Basic validation for paid registration
            if (formData.registration_type === 'paid') {
                const fee = Number(formData.entry_fee)
                if (!fee || fee <= 0) {
                    toast.error('Entry fee is required and must be greater than 0 for paid registrations')
                    setLoading(false)
                    return
                }
                if (!formData.entry_fee_type || !['ev', 'trp'].includes(formData.entry_fee_type)) {
                    toast.error('Entry fee type must be ev or trp')
                    setLoading(false)
                    return
                }
            }

            // Prepare payload with free-reset logic without duplicating keys
            const payload = {
                ...formData,
                ...(formData.registration_type === 'free' ? { entry_fee: 0, entry_fee_type: 'ev' } : {})
            };

            // Append all data to form data
            Object.keys(payload).forEach(key => {
                if ((key === 'landscape_img' || key === 'portrait_img') && !(payload[key] instanceof File)) {
                    return
                }
                if (key === "prizes") {
                    data.append(key, JSON.stringify(payload[key]))
                } else if (key === "details") {
                    data.append(key, JSON.stringify(payload[key]))
                } else if (key === "scoring_config") {
                    data.append(key, JSON.stringify(payload[key]))
                } else {
                    data.append(key, payload[key])
                }
            })

            if (edit) {
                await updateTournament(id, data)
                toast.success("Tournament Updated Successfully")
                return navigate(Page.TOURNAMENT)
            }

            await createTournament(data)
            toast.success("Tournament Created Successfully")
            navigate(Page.TOURNAMENT)
        } catch (error) {
            toast.error(error.message)
        }
        setLoading(false)
    }

    return <Box sx={{ width: "100%" }}>
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Box>
                    <Stack spacing={3}>
                        <TextField
                            name="title"
                            value={title} placeholder="Enter title"
                            onChange={handleChange}
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="level_type_select">Map Rotation</InputLabel>
                            <Select labelId='level_type_select' name="level_type" value={level_type} onChange={handleChange}>
                                {levelOptions?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="team_type_select">Team Type</InputLabel>
                            <Select labelId="team_type_select" name="team_type" value={team_type} onChange={handleChange}>
                                {teamOptions?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            name='team_count'
                            value={team_count}
                            placeholder='No of teams per tournament'
                            type="number"
                            fullWidth
                            onChange={handleChange}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="category_select">Select Game</InputLabel>
                            <Select labelId="category_select" name="category" value={category} onChange={handleChange}>
                                {categories?.map((category) =>
                                    <MenuItem key={category._id} value={category._id}>
                                        {category.game_title}
                                    </MenuItem>
                                )}

                            </Select>
                        </FormControl>
                    </Stack>
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Box>
                    <Stack spacing={3}>
                        <FormControl fullWidth>
                            <InputLabel id="registration_type_select">Registration Type</InputLabel>
                            <Select labelId='registration_type_select' name="registration_type" value={registration_type} onChange={handleChange}>
                                <MenuItem value={'free'}>Free</MenuItem>
                                <MenuItem value={'paid'}>Paid</MenuItem>
                            </Select>
                        </FormControl>
                        {registration_type === 'paid' && (
                            <>
                                <TextField
                                    name='entry_fee'
                                    value={entry_fee}
                                    placeholder='Entry fee (coins)'
                                    type="number"
                                    fullWidth
                                    onChange={handleChange}
                                />
                                <FormControl fullWidth>
                                    <InputLabel id="entry_fee_type_select">Entry Fee Type</InputLabel>
                                    <Select labelId="entry_fee_type_select" name="entry_fee_type" value={entry_fee_type} onChange={handleChange}>
                                        <MenuItem value={'ev'}>EV</MenuItem>
                                        <MenuItem value={'trp'}>TRP</MenuItem>
                                    </Select>
                                </FormControl>
                            </>
                        )}
                        <FormControlLabel control={<Switch checked={!!formData.subscriberOnly} onChange={e=>setFormData({...formData, subscriberOnly:e.target.checked})}/>} label='Subscriber Only' />
                        <TextField
                            name="registration_closed_on"
                            value={registration_closed_on}
                            placeholder='Enter Closing Date'
                            type='date'
                            onChange={handleChange}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="status_select">Status</InputLabel>
                            <Select labelId="status_select" name="status" value={status} onChange={handleChange}>
                                {tournamentStatuses?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            name="landscape_img"
                            label="Landscape Image"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            variant="filled"
                            size="small"

                            type='file'
                            placeholder='Portrait Image'
                            onChange={handleChange}
                        />
                        {formData.landscape_img && typeof formData.landscape_img === 'string' && (
                            <Box component="img" src={formData.landscape_img} alt="Current landscape" sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 1 }} />
                        )}
                        <TextField
                            name="portrait_img"
                            label="Portrait Image"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            variant="filled"
                            size="small"
                            type='file'
                            placeholder='Enter Landscape Portrait Image'
                            onChange={handleChange}
                        />
                        {formData.portrait_img && typeof formData.portrait_img === 'string' && (
                            <Box component="img" src={formData.portrait_img} alt="Current portrait" sx={{ width: 180, maxHeight: 220, objectFit: 'cover', borderRadius: 1 }} />
                        )}
                    </Stack>
                </Box>
            </Grid>
            {/* Details */}
            <Grid item xs={12}>
                <Stack spacing={3}>
                    <Typography variant="h4">Tournament Details</Typography>
                    <TextField
                        value={details.rules}
                        placeholder='Enter Rules'
                        multiline
                        onChange={(e) => {
                            setFormData({
                                ...formData,
                                details: {
                                    ...details,
                                    rules: e.target.value
                                }
                            })
                        }}
                        fullWidth
                    />
                    <TextField
                        value={details.updates}
                        placeholder='Enter Updates'
                        multiline
                        onChange={(e) => {
                            setFormData({
                                ...formData,
                                details: {
                                    ...details,
                                    updates: e.target.value
                                }
                            })
                        }}
                        fullWidth
                    />
                    <TextField
                        label="Roadmap"
                        value={formData.roadmap}
                        placeholder="Enter tournament roadmap"
                        multiline
                        minRows={4}
                        onChange={(e) => setFormData({ ...formData, roadmap: e.target.value })}
                        fullWidth
                    />
                     <Typography variant="h5">Scoring Rules</Typography>
                     <TextField
                         label="Kill points"
                         type="number"
                         value={scoring_config.kill_points}
                         onChange={(e) => setFormData({ ...formData, scoring_config: { ...scoring_config, kill_points: Number(e.target.value) } })}
                     />
                     <Typography variant="subtitle1">Position points</Typography>
                     <Stack spacing={3}>
                         {scoring_config.position_points?.map((point, index) =>
                             <Stack direction="row" spacing={3} key={index}>
                                 <TextField value={point.position} type="number" name="position" label="Position" onChange={(e) => handlePositionPointsChange(e, index)} />
                                 <TextField value={point.points} type="number" name="points" label="Points" onChange={(e) => handlePositionPointsChange(e, index)} />
                                 <IconButton onClick={() => setFormData({ ...formData, scoring_config: { ...scoring_config, position_points: scoring_config.position_points.filter((_, i) => i !== index) } })}>
                                     <AiFillDelete />
                                 </IconButton>
                             </Stack>
                         )}
                         <Button onClick={() => setFormData({ ...formData, scoring_config: { ...scoring_config, position_points: [...scoring_config.position_points, { position: scoring_config.position_points.length + 1, points: 0 }] } })}>Add Position</Button>
                     </Stack>
                     <Typography variant="h5">Points Distribution</Typography>
                    <Stack spacing={3}>
                        {details.points_distribution?.map((point, index) =>
                            <Stack direction="row" spacing={3} key={index}>
                                <TextField
                                    value={point.title}
                                    placeholder='Title'
                                    name="title"
                                    onChange={(e) => handlePrizeDistributionChange(e, index)}
                                />
                                <TextField
                                    value={point.points}
                                    type='number'
                                    name="points"
                                    placeholder='Points (coins)'
                                    onChange={(e) => handlePrizeDistributionChange(e, index)}
                                />
                                <IconButton
                                    onClick={() => setFormData({
                                        ...formData,
                                        details: {
                                            ...details,
                                            points_distribution: details.points_distribution.filter((_, i) => i !== index)
                                        }
                                    })}
                                >
                                    <AiFillDelete />
                                </IconButton>
                            </Stack>
                        )}
                        <Button
                            onClick={() => setFormData({
                                ...formData,
                                details: {
                                    ...details,
                                    points_distribution: [...details.points_distribution, {
                                        id: nanoid(10),
                                        title: "",
                                        points: ""
                                    }]
                                }
                            })}
                        > Add Point Distribution </Button>
                    </Stack>
                </Stack>
            </Grid>
            <Grid item xs={12}>
                <Stack spacing={3}>
                    <Typography variant="h4">Prizes</Typography>
                    <Stack spacing={3}>
                        {prizes?.map((prize, index) =>
                            <Stack direction="row" spacing={3} key={index}>
                                <TextField
                                    value={prize.title}
                                    placeholder='Title'
                                    name="title"
                                    onChange={e => handlePrizeChange(e, index)}
                                />
                                <TextField
                                    value={prize.price}
                                    type='number'
                                    placeholder='Price (coins)'
                                    name="price"
                                    onChange={e => handlePrizeChange(e, index)}

                                />
                                <IconButton
                                    onClick={() => setFormData({
                                        ...formData,
                                        prizes: prizes.filter((_, i) => i !== index)
                                    })}
                                >
                                    <AiFillDelete />
                                </IconButton>
                            </Stack>
                        )}
                        <Button
                            onClick={() => setFormData({
                                ...formData,
                                prizes: [...prizes, {
                                    title: "",
                                    price: ""
                                }]
                            })}
                        > Add Prize </Button>
                    </Stack>
                </Stack>
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">Tags</InputLabel>
                    <Select onChange={(e) => {
                        let tag = e.target.value
                        if (tags.includes(e.target.value)) return
                        let newTags = [...tags, tag]
                        setFormData({ ...formData, tags: newTags })
                    }}>
                        {defaultTags.map(tag => <MenuItem value={tag} key={tag}>{tag}</MenuItem>)}
                    </Select>
                </FormControl>
                {
                    tags.map(tag =>
                        <Box key={tag}
                            sx={{ width: "400px", background: "#eee", height: "60px", display: "flex", justifyContent: "space-around", alignItems: 'center', marginY: "1rem" }}>
                            <Typography variant="h6">{tag}</Typography>
                            <Button color={"secondary"}
                                onClick={() => {
                                    let currentTagToBeDeleted = tag
                                    let newTags = tags.filter(tag => tag !== currentTagToBeDeleted)
                                    setFormData({ ...formData, tags: newTags })
                                }}>Remove Tag</Button>
                        </Box>
                    )
                }
            </Grid>
        </Grid>
        <br />
    <LoadingButton variant="contained" disabled={disabled} onClick={handleSubmit} loading={loading}>{edit ? "Edit" : "Create"} Tournament</LoadingButton>
        <Button variant="outlined" color="secondary" onClick={() => navigate(`/tournament`)
        }>Go Back</Button>
    </Box>
}
